import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    T,
    balancesManager,
    positionsManager,
    ordersManager,
    Order,
    Position
} from '../imports';

/**
 * PortfolioContext provides access to the user's trading portfolio data.
 * It wraps three separate managers: BalancesManager, PositionsManager, OrdersManager.
 */
interface PortfolioContextType {
    balances: T.Balances | null;
    positions: Map<string, Position>;
    orders: Map<string, Order>;
    isConnected: boolean;
    isRefreshing: boolean;
}

const PortfolioContext = createContext<PortfolioContextType>({
    balances: null,
    positions: new Map(),
    orders: new Map(),
    isConnected: false,
    isRefreshing: false
});

/**
 * Hook to access portfolio data and connection status from any component.
 * Returns the current portfolio state with balances, positions, and orders.
 */
export const usePortfolio = (): PortfolioContextType => {
    const ctx = useContext(PortfolioContext);
    if (!ctx) throw new Error('usePortfolio must be used within a PortfolioProvider');
    return ctx;
};

/**
 * Provider component that wraps the app and provides portfolio context.
 * Must be placed after UserProvider since portfolio loading depends on authentication.
 * Manages state for all three managers: Balances, Positions, Orders.
 */
export const PortfolioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [balances, setBalances] = useState<T.Balances | null>(null);
    const [positions, setPositions] = useState<Map<string, Position>>(new Map());
    const [orders, setOrders] = useState<Map<string, Order>>(new Map());
    const [isConnected, setIsConnected] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        // Set up callbacks for manager updates
        balancesManager.onBalancesUpdated = (updatedBalances: T.Balances | null) => {
            setBalances(updatedBalances);
            setIsConnected(balancesManager.isConnected);
        };

        positionsManager.onPositionsUpdated = (updatedPositions: Map<string, Position>) => {
            setPositions(new Map(updatedPositions)); // Create new Map reference for React
            setIsConnected(positionsManager.isConnected);
        };

        ordersManager.onOrdersUpdated = (updatedOrders: Map<string, Order>) => {
            setOrders(new Map(updatedOrders)); // Create new Map reference for React
            setIsConnected(ordersManager.isConnected);
        };

        return () => {
            balancesManager.onBalancesUpdated = null;
            positionsManager.onPositionsUpdated = null;
            ordersManager.onOrdersUpdated = null;
        };
    }, []);

    // Provide context with all portfolio data
    return (
        <PortfolioContext.Provider value={{
            balances,
            positions,
            orders,
            isConnected: isConnected && balancesManager.isConnected && positionsManager.isConnected && ordersManager.isConnected,
            isRefreshing
        }}>
            {children}
        </PortfolioContext.Provider>
    );
};
